import React, { useState, useRef, useEffect, useMemo } from "react";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./erp_if_log_detail.scoped.scss";

interface State {
  logErpIfNo: string
}

const defState = {
  logErpIfNo: ""
};

interface Info {
  logErpIfNo: string,
  ifDate: string,
  ifType: string,
  ifAutoYn: string,
  ifResult: string,
  ifDataTotalQty: string,
  ifDataSuccessQty: string,
  ifDataFailQty: string,
  reqUserName: string,
  errorContent: string,
  detailInfos: any[]
}

const defInfo = {
  logErpIfNo: "",
  ifDate: "",
  ifType: "",
  ifAutoYn: "",
  ifResult: "",
  ifDataTotalQty: "",
  ifDataSuccessQty: "",
  ifDataFailQty: "",
  reqUserName: "",
  errorContent: "",
  detailInfos: []
};

function ErpIfLogDetail(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const location = useTabsStore((state) => state.getLocation(props.id));
  const query = useMemo(() => Object.fromEntries(new URLSearchParams(location.search).entries()), [location.search]);
  const gridStateKey = useMemo(() => {
      if (query.ifType == "IF-0001" || query.ifType == "IF-0003") {
        return location.path + ".req";
      } else if (query.ifType == "IF-0002" || query.ifType == "IF-0004") {
        return location.path + ".status";
      } else if (query.ifType == "IF-0006" || query.ifType == "IF-0007") {
        return location.path + ".change";
      } else if (query.ifType == "IF-0005" || query.ifType == "IF-0008" || query.ifType == "IF-0009") {
        return location.path + ".file";
      } else {
        return "";
      }
  }, [location.path, query.ifType]);

  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), [gridStateKey]);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "번호", field: "no", width: 50, cellStyle: {textAlign: "center"} },
    { headerName: "성공여부", field: "successYn", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("successYn") },
    { headerName: "오류메시지", headerClass: "hd-ta-left", field: "errorContent", width: 300, cellStyle: {textAlign: "left"} },

    // IF-0001(입고지시), IF-0003(출고지시)
    ...(gridStateKey.endsWith(".req") ? [
      { headerName: "Tracking ID", headerClass: "hd-ta-left", field: "tracking_id", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI ID", headerClass: "hd-ta-left", field: "eai_id", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F CODE", headerClass: "hd-ta-left", field: "if_code", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI CODE", headerClass: "hd-ta-left", field: "eai_code", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "창고코드", headerClass: "hd-ta-left", field: "subinv_code", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "상태 구분", headerClass: "hd-ta-left", field: "order_type", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "파트넘버 정보", headerClass: "hd-ta-left", field: "item_code", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "TR넘버 정보", headerClass: "hd-ta-left", field: "tr_no", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "시리얼넘버 정보", headerClass: "hd-ta-left", field: "serial_no", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "키워드", headerClass: "hd-ta-left", field: "description", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "카테고리", headerClass: "hd-ta-left", field: "category_code", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "지시 수량", headerClass: "hd-ta-left", field: "order_qty", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "현재고", headerClass: "hd-ta-left", field: "onhand_qty", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "단위", headerClass: "hd-ta-left", field: "uom", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "시효정보", headerClass: "hd-ta-left", field: "expire_date", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "요청자", headerClass: "hd-ta-left", field: "requestor", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "요청사항", headerClass: "hd-ta-left", field: "remark", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "워크샵 정보", headerClass: "hd-ta-left", field: "work_shop", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "워크오더 정보", headerClass: "hd-ta-left", field: "work_order", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "비짓넘버 정보", headerClass: "hd-ta-left", field: "visit_no", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "기번 정보", headerClass: "hd-ta-left", field: "hl_no", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "긴급 여부", headerClass: "hd-ta-left", field: "urgent_value", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "TO ORG", headerClass: "hd-ta-left", field: "to_org", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "TO SUBINV", headerClass: "hd-ta-left", field: "to_subinv", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "CONDITION", headerClass: "hd-ta-left", field: "condition", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "DELIVERY TO", headerClass: "hd-ta-left", field: "delivery_to", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "REMARK", headerClass: "hd-ta-left", field: "remark_1", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "TO LOCATOR", headerClass: "hd-ta-left", field: "attribute_1", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "DEPT", headerClass: "hd-ta-left", field: "attribute_2", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F 일시 정보", headerClass: "hd-ta-left", field: "if_date", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "생성일시 정보", headerClass: "hd-ta-left", field: "create_date", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "수정일시 정보", headerClass: "hd-ta-left", field: "update_date", width: 100, cellStyle: {textAlign: "left"} },
    ] : []),
    // IF-0002(입고상태변경), IF-0004(출고상태변경)
    ...(gridStateKey.endsWith(".status") ? [
      { headerName: "응답결과", headerClass: "hd-ta-left", field: "retStatus", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "응답메시지", headerClass: "hd-ta-left", field: "retMessage", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "응답일시", headerClass: "hd-ta-left", field: "retIfDate", width: 100, cellStyle: {textAlign: "left"} },

      { headerName: "TRACKING_ID", headerClass: "hd-ta-left", field: "req.trackingId", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI PK", headerClass: "hd-ta-left", field: "req.eaiId", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F CODE", headerClass: "hd-ta-left", field: "req.ifCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI CODE", headerClass: "hd-ta-left", field: "req.eaiCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "창고코드", headerClass: "hd-ta-left", field: "req.subinvCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "상태", headerClass: "hd-ta-left", field: "req.status", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "요청메시지", headerClass: "hd-ta-left", field: "req.message", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "요청자", headerClass: "hd-ta-left", field: "req.requestor", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F 일시 정보", headerClass: "hd-ta-left", field: "req.ifDate", width: 100, cellStyle: {textAlign: "left"} },
    ] : []),
    // IF-0006(SN변경), IF-0007(TR변경)
    ...(gridStateKey.endsWith(".change") ? [
      { headerName: "응답결과", headerClass: "hd-ta-left", field: "retStatus", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "응답메시지", headerClass: "hd-ta-left", field: "retMessage", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "응답일시", headerClass: "hd-ta-left", field: "retIfDate", width: 100, cellStyle: {textAlign: "left"} },

      { headerName: "TRACKING_ID", headerClass: "hd-ta-left", field: "req.trackingId", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI PK", headerClass: "hd-ta-left", field: "req.eaiId", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F CODE", headerClass: "hd-ta-left", field: "req.ifCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "EAI CODE", headerClass: "hd-ta-left", field: "req.eaiCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "I/F 일시 정보", headerClass: "hd-ta-left", field: "req.ifDate", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "창고코드", headerClass: "hd-ta-left", field: "req.subinvCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "작업구분", headerClass: "hd-ta-left", field: "req.orderType", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "변경 P/N 정보", headerClass: "hd-ta-left", field: "req.itemCode", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "변경 TR No 정보", headerClass: "hd-ta-left", field: "req.trNo", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "변경 S/N 정보", headerClass: "hd-ta-left", field: "req.serialNo", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "수량 정보", headerClass: "hd-ta-left", field: "req.qty", width: 100, cellStyle: {textAlign: "left"} },
      { headerName: "요청자", headerClass: "hd-ta-left", field: "req.requestor", width: 100, cellStyle: {textAlign: "left"} },
    ] : []),
    // IF-0005(재고조회), IF-0008(TR파일수신), IF-0009(티켓파일수신)
    ...(gridStateKey.endsWith(".file") ? [
      { headerName: "파일명", headerClass: "hd-ta-left", field: "fileName", width: 300, cellStyle: {textAlign: "left"} },
    ] : []),
  ], [gridStateKey]);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);
  
  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const query = Object.fromEntries(new URLSearchParams(location.search).entries());
    state.logErpIfNo = query.logErpIfNo || "";
    setState({ ...state });
  }

  async function searchInfo() {
    if (!state.logErpIfNo) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/log/erp_if_log_detail.data?mode=search", {logErpIfNo: state.logErpIfNo}, {});
      (data.detailInfos as any[]).forEach((info, idx) => {
        try {
          info.no = idx + 1;
          const detailInfo = JSON.parse(info.detailData || "{}");
          if (detailInfo.BodyType) {
            kutil.copyObj(detailInfo.BodyType[0], info, Object.keys(info));
          } else {
            kutil.copyObj(detailInfo, info, Object.keys(info));
          }
        } catch(e) {
          console.log(e);
        }
      });
      setInfo({
        ...data.info,
        detailInfos: data.detailInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/log/erp_if_log_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>ERP I/F 로그</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={goList}>목록</Button>
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 기본정보 */}
        <div className="table-wrap emp">
          <table>
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">I/F 일자</th>
                <td>
                  { kutil.date2string(info.ifDate, "yyyy-MM-dd HH:mm:ss") }
                </td>
                <th scope="row">I/F 항목</th>
                <td>
                  { commonCode.getCodeValue("ifType", info.ifType) }
                </td>
                <th scope="row">I/F 구분</th>
                <td>
                  { commonCode.getCodeValue("ifAutoYn", info.ifAutoYn) }
                </td>
                <th scope="row">성공여부</th>
                <td>
                  { commonCode.getCodeValue("ifResult", info.ifResult) }
                </td>
              </tr>
              <tr>
                <th scope="row">I/F 데이터<br />(전체/성공/실패)</th>
                <td>
                  { kutil.setComma(info.ifDataTotalQty) }/{ kutil.setComma(info.ifDataSuccessQty) }/{ kutil.setComma(info.ifDataFailQty) }
                </td>
                <th scope="row">요청자</th>
                <td>
                  { info.reqUserName }
                </td>
                <td colSpan={4}></td>
              </tr>
              <tr>
                <th scope="row">오류메시지</th>
                <td colSpan={7}>
                  <pre>{ info.errorContent }</pre>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/*// 기본정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>상세정보</p>
        </div>
        {/* 상세정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              key={gridStateKey}
              columnDefs={gridInfo}
              rowData={info.detailInfos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 상세정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(ErpIfLogDetail);
